import { MutableRefObject, ReactNode, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Hook that checks if an element is scrollable
const useIsScrollable = (dependencies: ReactNode[], wrapperRef: MutableRefObject<HTMLElement | Element | null>, getFirstChild: boolean) => {
  const [node, setNode] = useState(wrapperRef?.current);
  const [isScrollable, setIsScrollable] = useState(node ? node.scrollHeight > node.clientHeight : false);

  const wrapperCurrent = wrapperRef?.current;

  useLayoutEffect(() => {
    if (getFirstChild) {
      setNode(wrapperCurrent?.children[0] || null);
    } else {
      setNode(wrapperCurrent);
    }
  }, [getFirstChild, wrapperCurrent]);

  useLayoutEffect(() => {
    if (!node) return;

    setIsScrollable(node.scrollHeight > node.clientHeight);
    /**
     * The below eslint-disable is necessary in order to accept the spread operator in the dependencies array.
     * This useLayoutEffect needs each child of the elements in the list as a separate dependency in this useLayoutEffect
     * because their height may change and the isScrollable needes to be recalculated even if a single child has updated.
     *
     * If we use it without the spread operator it will fire on every render if you consider that "[1] === [1]" returns false
     */
  }, [...dependencies, node]);

  useLayoutEffect(() => {
    if (!node) return;

    const handleWindowResize = () => {
      setIsScrollable(node.scrollHeight > node.clientHeight);
    };

    window.addEventListener('resize', handleWindowResize);

    return () => window.removeEventListener('resize', handleWindowResize);
  }, [node]);

  return [isScrollable, node];
};

const useScrollCheck = (
  ref: MutableRefObject<HTMLElement | Element | null>,
  triggerHeightFromEndPixels: number,
  getFirstChild: boolean,
) => {
  const element: HTMLElement | Element | null = getFirstChild ? ref?.current?.children[0] || null : ref?.current || null;

  const [triggered, setTriggered] = useState(false);

  useLayoutEffect(() => {
    if (!element) return;

    const pixelsToScrollCheck = (el: HTMLElement | Element) => {
      if (typeof triggerHeightFromEndPixels !== 'number') return false;

      if (typeof el.clientHeight !== 'number' || el.clientHeight === 0) return false;

      const result = el.scrollHeight - el.clientHeight - el.scrollTop <= triggerHeightFromEndPixels;
      return result;
    };

    const listenerCallback = () => {
      setTriggered(pixelsToScrollCheck(element));
    };

    element.addEventListener('scroll', listenerCallback, false);
    window.addEventListener('resize', listenerCallback);

    // Cleanup
    return () => {
      element.removeEventListener('scroll', listenerCallback, false);
      window.removeEventListener('resize', listenerCallback);
    };
  }, [element, triggerHeightFromEndPixels]);

  return [triggered];
};

/**
 * @see https://v5.reactrouter.com/web/example/query-parameters
 */
function useLocationQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export { useIsScrollable, useLocationQuery, useScrollCheck };
